/**
 * Which surface the user wants: the desktop app, the mobile app at `/m`, or
 * whichever the viewport implies.
 *
 * Shared by both surfaces so there is one definition of the preference. The key
 * is ALSO read by the inline redirect script at the top of `index.html`, which
 * runs before any module loads and therefore cannot import this file — keep the
 * literal below in sync with it.
 *
 * **Session-scoped on purpose.** `MV-PLT-012` requires the choice to be
 * remembered "for the session only", and the devices this runs on are shared
 * cleanroom handsets (`MV-SEC-021`): an operator who forces desktop must not be
 * able to strand the next person there. Closing the tab returns everyone to
 * Auto. This replaced an earlier `localStorage['cgt.forceDesktop']` opt-out that
 * persisted indefinitely.
 */

import { isFramed, topWindow } from './topWindow'

export type ViewMode = 'auto' | 'desktop' | 'tablet' | 'mobile'

/**
 * Labels, in presentation order.
 *
 * Plain data with no icons, and it lives here rather than beside a component on
 * purpose. The desktop control and the mobile controls are in SEPARATE modules
 * because sharing one module put its Radix popover import into the chunk both
 * entries load, costing the mobile bundle 16 KB gzipped for a control it never
 * renders. Only the wording is shared; each surface brings its own icons.
 */
export const VIEW_MODES: readonly ViewMode[] = ['auto', 'desktop', 'tablet', 'mobile']

/**
 * `tablet` is labelled iPad because that is the device people name, but it is
 * any tablet: it is the operator view in its tablet layout (z8nrz7g204). Auto
 * picks it on a touch tablet by itself; the choice exists for a demo on a
 * laptop or a monitor, where nothing can detect a tablet.
 */
export const VIEW_MODE_META: Record<ViewMode, { label: string; hint: string }> = {
  auto: { label: 'Auto', hint: 'Follow the device' },
  desktop: { label: 'Desktop', hint: 'Always the full site' },
  tablet: { label: 'iPad', hint: 'Always the operator view, tablet layout' },
  mobile: { label: 'Mobile', hint: 'Always the operator view, phone layout' },
}

/** Must match the literal in the inline script in `index.html`. */
export const VIEW_MODE_KEY = 'cgt.viewMode'

/** The superseded localStorage key, cleared on boot so it cannot linger. */
export const LEGACY_FORCE_DESKTOP_KEY = 'cgt.forceDesktop'

/** Below this width the viewport is treated as a phone. Mirrors `MV-PLT-010`. */
export const MOBILE_MAX_WIDTH = 767

/**
 * A device whose main pointer is a finger: a phone or a tablet, never a laptop
 * with a mouse or trackpad (an iPad with a keyboard case still reports coarse).
 * Auto sends such a device to the operator view at any width (z8nrz7g204).
 */
export const TOUCH_DEVICE_QUERY = '(pointer: coarse)'

/**
 * Narrows an untrusted stored value. Anything unrecognised is Auto, so a
 * corrupted or hand-edited entry cannot pin someone to a surface.
 */
export function parseViewMode(raw: string | null | undefined): ViewMode {
  return raw === 'desktop' || raw === 'mobile' || raw === 'tablet' ? raw : 'auto'
}

/**
 * Which entry a mode resolves to. Pure: the whole redirect rule in one place.
 * Auto opens the operator view on a narrow screen and on a touch device (a
 * tablet); a wide screen with a mouse gets the full site.
 */
export function resolveSurface(
  mode: ViewMode,
  isNarrowViewport: boolean,
  isTouchDevice = false,
): '/' | '/m/' {
  if (mode === 'desktop') return '/'
  if (mode === 'mobile' || mode === 'tablet') return '/m/'
  return isNarrowViewport || isTouchDevice ? '/m/' : '/'
}

/** Which entry is currently running, from the path. `/my-steps` is not `/m`. */
export function surfaceOf(pathname: string): '/' | '/m/' {
  return pathname === '/m' || pathname.startsWith('/m/') ? '/m/' : '/'
}

/**
 * The URL that actually SERVES a surface, as opposed to the surface's identity.
 *
 * STOPGAP. The two differ for mobile because CloudFront cannot serve a
 * directory index: `default_root_object` applies only to `/`, so `/m/` is a
 * missing S3 key, 403s, and the distribution's error rules answer with the
 * desktop app at 200. `/m/index.html` is the object that exists. An inline
 * script in `m/index.html` puts the tidy `/m/` back in the address bar once the
 * real app is running.
 *
 * Revert this to a plain `/m/` when the CloudFront Function rewriting
 * `/m` and `/m/*` -> `/m/index.html` ships. See the deployment note in
 * CLAUDE.md.
 */
export const SURFACE_ENTRY_URL: Record<'/' | '/m/', string> = {
  '/': '/',
  '/m/': '/m/index.html',
}

export function getViewMode(): ViewMode {
  try {
    return parseViewMode(sessionStorage.getItem(VIEW_MODE_KEY))
  } catch {
    // Private mode / storage disabled: no preference is expressible.
    return 'auto'
  }
}

export function storeViewMode(mode: ViewMode): void {
  try {
    if (mode === 'auto') sessionStorage.removeItem(VIEW_MODE_KEY)
    else sessionStorage.setItem(VIEW_MODE_KEY, mode)
  } catch {
    // Without storage the choice cannot stick; the caller still navigates, so
    // the control does something for this page view.
  }
}

export function isNarrowViewport(): boolean {
  return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches
}

export function isTouchDevice(): boolean {
  return window.matchMedia(TOUCH_DEVICE_QUERY).matches
}

/**
 * Fired on `window` when the stored mode changes without a page load (iPad and
 * Mobile are the same surface), so the operator view can redraw its layout.
 */
export const VIEW_MODE_EVENT = 'cgt:viewmode'

/**
 * Persist the choice and move to the surface it implies.
 *
 * A full page load, not a router navigation: the two surfaces are separate html
 * entries with separate bundles, so there is nothing to push to. When the
 * current surface already matches, nothing happens beyond the store — switching
 * to Auto on a wide desktop screen should not reload the page you are reading.
 */
export function applyViewMode(mode: ViewMode): void {
  const stagedBefore = staged(getViewMode())
  storeViewMode(mode)
  // Inside the iPad view stage's frame (z8nrz7g204) the choice is for the
  // page holding the frame: that page decides between stage and app.
  const host = isFramed() ? topWindow() : null
  if (host) {
    const target = resolveSurface(mode, false, false)
    if (surfaceOf(host.location.pathname) !== target) host.location.assign(SURFACE_ENTRY_URL[target])
    else host.location.reload()
    return
  }
  const target = resolveSurface(mode, isNarrowViewport(), isTouchDevice())
  if (surfaceOf(window.location.pathname) !== target) {
    // The surface is the identity, `SURFACE_ENTRY_URL` is what resolves.
    window.location.assign(SURFACE_ENTRY_URL[target])
  } else if (target === '/m/' && staged(mode) !== stagedBefore) {
    // Into or out of the iPad stage: the stage is decided at boot.
    window.location.reload()
  } else {
    window.dispatchEvent(new Event(VIEW_MODE_EVENT))
  }
}

/**
 * The operator view draws the iPad stage for this mode here: iPad chosen on a
 * device without touch. Mirrors `shouldStage` in `src/mobile/lib/tabletStage.ts`.
 */
function staged(mode: ViewMode): boolean {
  return mode === 'tablet' && !isTouchDevice()
}


/** Drop the superseded persistent opt-out. Called once per boot on each entry. */
export function clearLegacyOptOut(): void {
  try {
    localStorage.removeItem(LEGACY_FORCE_DESKTOP_KEY)
  } catch {
    // Nothing stored, nothing to clear.
  }
}
